import { Cookie } from 'lucide-react';
import { Link } from 'react-router-dom';

const EFFECTIVE = '14 May 2025';
const PRIVACY_EMAIL = 'privacy@transitdeal.co.uk';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-100">{title}</h2>
      <div className="space-y-3 text-sm text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}

interface CookieRow {
  name: string;
  purpose: string;
  duration: string;
  category: 'Essential' | 'Functional' | 'Analytics';
  setBy: string;
}

const COOKIES: CookieRow[] = [
  {
    name: 'oidc.user:{authority}:{clientId}',
    purpose: 'Stores your signed-in session, including identity and access tokens',
    duration: 'Session',
    category: 'Essential',
    setBy: 'TransitDeal (Keycloak)',
  },
  {
    name: 'KEYCLOAK_IDENTITY',
    purpose: 'Identifies your single sign-on session with the authentication server',
    duration: 'Session',
    category: 'Essential',
    setBy: 'TransitDeal (sign-in service)',
  },
  {
    name: 'KEYCLOAK_SESSION',
    purpose: 'Tracks the active sign-in session so you are not asked to sign in repeatedly',
    duration: 'Until session expires',
    category: 'Essential',
    setBy: 'TransitDeal (sign-in service)',
  },
  {
    name: 'AUTH_SESSION_ID',
    purpose: 'Correlates the steps of a single sign-in attempt',
    duration: 'Session',
    category: 'Essential',
    setBy: 'TransitDeal (sign-in service)',
  },
  {
    name: 'td_consent',
    purpose: 'Records your cookie consent preferences so we do not ask again',
    duration: '12 months',
    category: 'Essential',
    setBy: 'TransitDeal',
  },
  {
    name: 'tanstack-query-cache (memory)',
    purpose: 'In-memory API response cache — improves page load speed. Not persisted across sessions.',
    duration: 'Session (in-memory only)',
    category: 'Functional',
    setBy: 'TransitDeal',
  },
];

const CATEGORY_COLORS: Record<string, string> = {
  Essential: 'bg-green-100 text-green-700',
  Functional: 'bg-blue-100 text-blue-700',
  Analytics: 'bg-purple-100 text-purple-700',
};

export default function CookiePolicyPage() {
  return (
    <div className="page-shell-prose">
      {/* Header */}
      <div className="flex items-start gap-4 mb-8">
        <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center shrink-0 mt-1">
          <Cookie className="w-5 h-5 text-yellow-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cookie Policy</h1>
          <p className="text-sm text-gray-500 mt-1">
            Effective date: {EFFECTIVE} &nbsp;·&nbsp; TransitDeal Ltd
          </p>
        </div>
      </div>

      <div className="card p-6 sm:p-8">

        <Section title="1. What Are Cookies?">
          <p>
            Cookies are small text files placed on your device by websites when you visit them. The UK's
            Privacy and Electronic Communications Regulations (PECR) and UK GDPR require us to tell you
            about the cookies (and similar storage technologies) we use, and to obtain your consent for
            any that are not strictly necessary.
          </p>
          <p>
            TransitDeal uses <strong>browser localStorage and sessionStorage</strong> rather than
            traditional HTTP cookies for most functionality. These serve the same purpose and are subject
            to the same rules under PECR as cookies.
          </p>
        </Section>

        <Section title="2. Categories of Storage We Use">
          <div className="space-y-5">
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-green-100 text-green-700">Essential</span>
                <span className="font-semibold text-gray-800 text-sm">Strictly Necessary</span>
              </div>
              <p className="text-sm text-gray-600">
                These are required for the platform to function. They handle authentication
                (signing you in and keeping you signed in), security, and remembering your cookie
                preference. <strong>They cannot be turned off.</strong>
              </p>
            </div>

            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-blue-100 text-blue-700">Functional</span>
                <span className="font-semibold text-gray-800 text-sm">Preference & Performance</span>
              </div>
              <p className="text-sm text-gray-600">
                These improve your experience by remembering preferences (such as voice assistant settings)
                and caching data to reduce loading times. If declined, some features may be slower or reset
                between sessions.
              </p>
            </div>

            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-purple-100 text-purple-700">Analytics</span>
                <span className="font-semibold text-gray-800 text-sm">Analytics &amp; Improvement</span>
              </div>
              <p className="text-sm text-gray-600">
                These help us understand how the platform is used so we can improve features. We collect
                anonymous, aggregated data only. Your consent is required before these are activated.
                Currently we do not use third-party analytics tools, but we may introduce them in the
                future with appropriate notice.
              </p>
            </div>
          </div>
        </Section>

        <Section title="3. Storage We Use — Full List">
          <p className="text-xs text-gray-500 mb-3">
            Note: entries shown as "&#123;authority&#125;" and "&#123;clientId&#125;" are replaced with your
            application's sign-in service and client identifier.
          </p>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-xs border-collapse min-w-[600px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Name / Key</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Purpose</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Duration</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Category</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Set by</th>
                </tr>
              </thead>
              <tbody>
                {COOKIES.map((c, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-gray-800 align-top">{c.name}</td>
                    <td className="px-3 py-2 text-gray-600 align-top">{c.purpose}</td>
                    <td className="px-3 py-2 text-gray-600 align-top whitespace-nowrap">{c.duration}</td>
                    <td className="px-3 py-2 align-top">
                      <span className={`px-2 py-0.5 rounded-full font-semibold ${CATEGORY_COLORS[c.category]}`}>
                        {c.category}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600 align-top">{c.setBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="4. Third-Party Storage">
          <p>
            Sign-in is handled by our own authentication service at{' '}
            <span className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">/auth</span>,
            which sets the session cookies listed above. It is operated by TransitDeal on our own
            infrastructure, so no third party receives your sign-in details and this is not
            third-party storage.
          </p>
          <p>
            <strong>Stripe</strong> (our payment processor) may set cookies during the payment flow on
            Stripe-hosted pages. These are governed by{' '}
            <a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              Stripe's Privacy Policy
            </a>.
          </p>
        </Section>

        <Section title="5. Managing Your Preferences">
          <p>You can manage your consent at any time by:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-600 ml-2">
            <li>
              Clicking <strong>"Manage"</strong> in the cookie banner at the bottom of the page (re-appears
              if your consent has expired or you clear your browser storage).
            </li>
            <li>
              Clearing your browser's localStorage/cookies via your browser settings — this will also sign
              you out of the platform.
            </li>
            <li>
              Contacting us at{' '}
              <a href={`mailto:${PRIVACY_EMAIL}`} className="text-blue-600 hover:underline">{PRIVACY_EMAIL}</a> to
              request erasure of consent records.
            </li>
          </ul>
          <p>
            Note: blocking essential storage will prevent the platform from working, as we rely on
            localStorage to maintain your authenticated session.
          </p>
        </Section>

        <Section title="6. Mobile App">
          <p>
            The TransitDeal mobile app (iOS and Android) does not use browser cookies. Instead, it stores
            authentication tokens securely in the device's encrypted keychain/keystore (via{' '}
            <span className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">expo-secure-store</span>).
            This storage is inaccessible to other apps and is essential for keeping you signed in.
          </p>
          <p>
            The mobile app may request device-level permissions (push notifications, microphone for voice
            features) separately — you control these through your device's Settings app.
          </p>
        </Section>

        <Section title="7. Changes to This Policy">
          <p>
            We may update this Cookie Policy when we change the cookies we use or as required by law.
            We will notify you by updating the effective date above and, where changes are material,
            by in-platform notice.
          </p>
        </Section>

        <Section title="8. More Information">
          <p>
            For more detail on how we process your personal data, see our{' '}
            <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>.
            For general information about cookies, visit{' '}
            <a href="https://ico.org.uk/for-the-public/online/cookies/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              ico.org.uk
            </a>.
          </p>
          <p>
            Contact us: <a href={`mailto:${PRIVACY_EMAIL}`} className="text-blue-600 hover:underline">{PRIVACY_EMAIL}</a>
          </p>
        </Section>
      </div>
    </div>
  );
}
